import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { dashboardLoginSchema, dashboardSignupSchema } from '../../components/Auth/shared/validationSchemas';
import SocialLoginButton from '../../components/Auth/shared/SocialLoginButton';
import FormInput from '../../components/Auth/shared/FormInput';
import ErrorDisplay from '../../components/Auth/shared/ErrorDisplay';
import { touchVenueDashboardActivity } from '../../utils/venueDashboardSession';
import venueApiService from '../../services/venueApi';
import { ReactComponent as OccasiaLogoIcon } from '../../components/icons/occasia-logo-icon.svg';
import '../../components/Landing2Header/Landing2Header.css';
import '../../components/Landing2Footer/Landing2Footer.css';
import '../../components/Auth/AuthModal.css';
import { usePageSeo } from '../../components/Seo/PageSeo';
import './DashboardLogin.css';

const TAB_LOGIN = 'login';
const TAB_REGISTER = 'register';

const LOGIN_BANNERS = {
  no_venue: 'No venue is linked to your account yet. If you own a venue, please contact support.',
  wrong_venue: "You don't have permission to access this venue's dashboard. You can only manage venues linked to your account.",
  not_onboarded: 'Not onboarded as a venue owner. If you own a venue, please contact support to link it to your account.',
  not_signed_in: 'Please log in to access the venue dashboard.',
  session_expired: 'Your venue dashboard session expired after 24 hours of inactivity. Please sign in again.',
  generic: 'Something went wrong while opening the venue dashboard. Please try again.',
};

const ownsUrlVenue = (profile, urlVenueId) =>
  Boolean(
    urlVenueId &&
    (profile?.owned_venues?.some((venue) => venue.id === urlVenueId) || profile?.venue_id === urlVenueId)
  );

const resolveDashboardVenueId = (profile, urlVenueId) => {
  if (!profile) return null;

  if (profile.is_admin) {
    return urlVenueId || profile.owned_venues?.[0]?.id || profile.venue_id || null;
  }

  if (profile.user_type === 'venue_owner') {
    return (ownsUrlVenue(profile, urlVenueId) ? urlVenueId : null)
      || profile.owned_venues?.[0]?.id
      || profile.venue_id
      || null;
  }

  return null;
};

const isVenueIdMismatch = (profile, urlVenueId) =>
  Boolean(profile && urlVenueId && !profile.is_admin && profile.user_type === 'venue_owner' && !ownsUrlVenue(profile, urlVenueId));

const DashboardLogin = () => {
  usePageSeo({
    title: 'Venue dashboard sign in',
    path: '/dashboard-login',
    noindex: true
  });

  const { loginWithEmail, loginWithGoogleOAuth, registerWithEmail, currentUser, userProfile, error: authError, clearError } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get('session') === 'expired';
  const urlVenueId = searchParams.get('venueId');
  const onboardingAccessToken = searchParams.get('access_token');
  const [activeTab, setActiveTab] = useState(TAB_LOGIN);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [venueName, setVenueName] = useState('');
  const [, setVenueLookup] = useState(urlVenueId ? 'loading' : 'idle');

  const loginForm = useForm({
    resolver: yupResolver(dashboardLoginSchema),
    defaultValues: { email: '', password: '' }
  });

  const registerForm = useForm({
    resolver: yupResolver(dashboardSignupSchema),
    defaultValues: { first_name: '', last_name: '', email: '', password: '', confirmPassword: '' }
  });

  useEffect(() => {
    const token = (onboardingAccessToken || '').trim();
    if (!token) return;
    localStorage.setItem('access_token', token);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('access_token');
    const qs = nextParams.toString();
    navigate(qs ? `/dashboard-login?${qs}` : '/dashboard-login', { replace: true });
    window.location.reload();
  }, [onboardingAccessToken, navigate, searchParams]);

  const redirectAuthenticatedUser = useCallback((profile) => {
    if (!profile) return false;

    // A failed dashboard open should stay on the login banner instead of bouncing back.
    if (searchParams.get('error') === 'generic') {
      setIsLoading(false);
      return false;
    }

    touchVenueDashboardActivity();

    if (profile.is_admin || profile.user_type === 'venue_owner') {
      if (isVenueIdMismatch(profile, urlVenueId)) {
        setIsLoading(false);
        return false;
      }
      const venueId = resolveDashboardVenueId(profile, urlVenueId);
      if (venueId) {
        navigate(`/venue-dashboard?venueId=${venueId}`);
        setIsLoading(false);
        return true;
      }
      setIsLoading(false);
      return false;
    }

    setIsLoading(false);
    return false;
  }, [navigate, searchParams, urlVenueId]);

  useEffect(() => {
    if (!userProfile) return;

    const redirected = redirectAuthenticatedUser(userProfile);

    if (
      !redirected &&
      searchParams.get('error') !== 'generic' &&
      userProfile.user_type === 'venue_owner' &&
      !isVenueIdMismatch(userProfile, urlVenueId)
    ) {
      const venueId = userProfile.owned_venues?.[0]?.id || userProfile.venue_id;
      if (!venueId) return undefined;
      const timeoutId = setTimeout(() => {
        navigate(`/venue-dashboard?venueId=${venueId}`);
        setIsLoading(false);
      }, 3000);
      return () => clearTimeout(timeoutId);
    }
  }, [userProfile, navigate, redirectAuthenticatedUser, searchParams, urlVenueId]);

  useEffect(() => {
    if (!urlVenueId) {
      setVenueName('');
      setVenueLookup('idle');
      return undefined;
    }

    let cancelled = false;
    setVenueName('');
    setVenueLookup('loading');

    venueApiService
      .getVenueById(urlVenueId)
      .then((venue) => {
        if (cancelled) return;
        if (!venue) {
          setVenueName('');
          setVenueLookup('missing');
          return;
        }
        const name = venue?.name || venue?.venue_name || '';
        setVenueName(typeof name === 'string' ? name.trim() : '');
        setVenueLookup('ok');
      })
      .catch(() => {
        if (cancelled) return;
        setVenueName('');
        setVenueLookup('missing');
      });

    return () => {
      cancelled = true;
    };
  }, [urlVenueId]);

  const clearAccessErrorQuery = () => {
    if (!searchParams.get('error') && !searchParams.get('detail')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('error');
    next.delete('detail');
    const qs = next.toString();
    navigate(qs ? `/dashboard-login?${qs}` : '/dashboard-login', { replace: true });
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    setError('');
    setRegistrationSuccess(false);
    clearError();
  };

  useEffect(() => {
    loginForm.reset();
    registerForm.reset();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const onLoginSubmit = async (data) => {
    setIsLoading(true);
    setError('');
    clearError();
    clearAccessErrorQuery();
    try {
      await loginWithEmail(data.email, data.password);
    } catch (err) {
      setError(err.message || authError || 'Failed to login. Please check your credentials.');
      setIsLoading(false);
    }
  };

  const onRegisterSubmit = async (data) => {
    setIsLoading(true);
    setError('');
    clearError();
    try {
      await registerWithEmail({
        email: data.email,
        password: data.password,
        first_name: data.first_name,
        last_name: data.last_name,
        role: 'venue_owner',
      });
      setRegistrationSuccess(true);
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError('');
    clearError();
    clearAccessErrorQuery();
    try {
      await loginWithGoogleOAuth();
    } catch (err) {
      if (!err?.message) {
        setError('Failed to login with Google. Please try again.');
      }
      setIsLoading(false);
    }
  };

  const isLogin = activeTab === TAB_LOGIN && !registrationSuccess;
  const isRegister = activeTab === TAB_REGISTER && !registrationSuccess;
  const derivedAccessError = (() => {
    if (!currentUser || !userProfile) return '';
    if (!userProfile.is_admin && userProfile.user_type !== 'venue_owner') {
      return LOGIN_BANNERS.not_onboarded;
    }
    if (isVenueIdMismatch(userProfile, urlVenueId)) return LOGIN_BANNERS.wrong_venue;
    if (!resolveDashboardVenueId(userProfile, urlVenueId)) return LOGIN_BANNERS.no_venue;
    return '';
  })();
  const errorCode = searchParams.get('error');
  const queryAccessError = errorCode === 'generic'
    ? LOGIN_BANNERS.generic
    : (LOGIN_BANNERS[errorCode] || '');
  const loginBanner = error
    || authError
    || derivedAccessError
    || queryAccessError
    || (sessionExpired ? LOGIN_BANNERS.session_expired : '');
  return (
    <div className={`dashboard-login-page ${isRegister ? 'auth-modal-signup' : 'auth-modal-login'}`}>
      <div className="brand-wash" aria-hidden>
        <div className="l2-footer-glow brand-wash--flip" />
        <div className="brand-wash-fade" />
      </div>
      <div className="l2-header-bar dashboard-login-header">
        <nav className="l2-header-nav">
          <Link to="/" className="l2-header-logo" aria-label="Occasia">
            <OccasiaLogoIcon className="l2-header-logo-svg" />
          </Link>
        </nav>
      </div>

      <div className="dashboard-login-content">

        {registrationSuccess && (
          <div className="auth-modal-panel dashboard-login-panel">
            <h1 className="dashboard-login-heading">
              <span className="dashboard-login-heading-hl">Check your email</span>
              <span className="dashboard-login-heading-muted">
                We've sent a verification link to your email address.
                Please check your inbox and click the link to activate your account.
              </span>
            </h1>
            <p className="dashboard-login-success-subtext">
              The link expires in 24 hours. Check your spam folder if you don't see it.
            </p>
            <button
              type="button"
              className="auth-submit-btn auth-submit-btn-signup"
              onClick={() => switchTab(TAB_LOGIN)}
            >
              Go to Login
            </button>
          </div>
        )}

        {isLogin && (
          <div className="auth-modal-panel dashboard-login-panel">
            <div className="dashboard-login-heading-block">
              {venueName ? (
                <p className="dashboard-login-eyebrow">{venueName}</p>
              ) : null}
              <h1 className="dashboard-login-heading">
                <span className="dashboard-login-heading-hl">Welcome back</span>
                <span className="dashboard-login-heading-muted">
                  Login to manage
                  <br />
                  your venue
                </span>
              </h1>
            </div>

            <div className="auth-modal-body auth-modal-body-login">
              <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} className="auth-form auth-form-login">
                <ErrorDisplay error={loginBanner} />
                <FormInput
                  type="email"
                  placeholder="Email"
                  register={loginForm.register}
                  error={loginForm.formState.errors.email}
                  name="email"
                  disabled={isLoading}
                />
                <FormInput
                  type="password"
                  placeholder="Password"
                  register={loginForm.register}
                  error={loginForm.formState.errors.password}
                  name="password"
                  disabled={isLoading}
                />
                <button
                  type="submit"
                  className="auth-submit-btn auth-submit-btn-login"
                  disabled={isLoading}
                >
                  {isLoading ? 'Logging in...' : 'Login'}
                </button>
              </form>

              <div className="auth-divider auth-divider-login">
                <div className="auth-divider-line"></div>
                <span className="auth-divider-text auth-divider-text-login">or</span>
                <div className="auth-divider-line"></div>
              </div>

              <div className="auth-social-buttons auth-social-buttons-login">
                <SocialLoginButton
                  provider="google"
                  onClick={handleGoogleLogin}
                  disabled={isLoading}
                  loadingText="Logging in..."
                >
                  Login with Google
                </SocialLoginButton>
              </div>
            </div>
          </div>
        )}

        {isRegister && (
          <div className="auth-modal-panel dashboard-login-panel">
            <h1 className="dashboard-login-heading">
              <span className="dashboard-login-heading-hl">Join Occasia</span>
              <span className="dashboard-login-heading-muted">
                Create a venue owner account to manage your space
              </span>
            </h1>

            <div className="auth-modal-body auth-modal-body-signup">
              <form onSubmit={registerForm.handleSubmit(onRegisterSubmit)} className="auth-form auth-form-signup">
                <div className="auth-form-row">
                  <FormInput
                    type="text"
                    placeholder="First name"
                    register={registerForm.register}
                    error={registerForm.formState.errors.first_name}
                    name="first_name"
                    disabled={isLoading}
                  />
                  <FormInput
                    type="text"
                    placeholder="Last name"
                    register={registerForm.register}
                    error={registerForm.formState.errors.last_name}
                    name="last_name"
                    disabled={isLoading}
                  />
                </div>
                <FormInput
                  type="email"
                  placeholder="Email"
                  register={registerForm.register}
                  error={registerForm.formState.errors.email}
                  name="email"
                  disabled={isLoading}
                />
                <FormInput
                  type="password"
                  placeholder="Password"
                  register={registerForm.register}
                  error={registerForm.formState.errors.password}
                  name="password"
                  disabled={isLoading}
                />
                <FormInput
                  type="password"
                  placeholder="Confirm password"
                  register={registerForm.register}
                  error={registerForm.formState.errors.confirmPassword}
                  name="confirmPassword"
                  disabled={isLoading}
                />
                <ErrorDisplay error={error || authError} />
                <button
                  type="submit"
                  className="auth-submit-btn auth-submit-btn-signup"
                  disabled={isLoading}
                >
                  {isLoading ? 'Creating account...' : 'Sign Up'}
                </button>
              </form>

              <div className="auth-terms auth-terms-signup">
                <p className="auth-terms-text-signup">
                  <span>By clicking Sign Up, you agree to </span>
                  <span>Occasia's</span>
                  <br />
                  <a href="/terms" className="auth-terms-link auth-terms-link-signup">Terms of use</a>
                  <span> </span>
                  <span>and </span>
                  <a href="/privacy" className="auth-terms-link auth-terms-link-signup">Privacy Policy</a>
                </p>
              </div>

              <div className="auth-divider auth-divider-signup">
                <div className="auth-divider-line"></div>
                <span className="auth-divider-text auth-divider-text-signup">or</span>
                <div className="auth-divider-line"></div>
              </div>

              <div className="auth-social-buttons auth-social-buttons-signup">
                <SocialLoginButton
                  provider="google"
                  onClick={handleGoogleLogin}
                  disabled={isLoading}
                  loadingText="Signing up..."
                >
                  Sign up with Google
                </SocialLoginButton>
              </div>

              <div className="auth-modal-footer auth-modal-footer-signup">
                <p className="auth-switch-text-signup">
                  Already have an account?{' '}
                  <button
                    type="button"
                    className="auth-switch-link-signup"
                    onClick={() => switchTab(TAB_LOGIN)}
                  >
                    Login
                  </button>
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardLogin;
